$(document).foundation();

class ServiceFormElement extends HTMLElement {
    /**
     * Promise which resolves after hitting the `services` endpoint
     * @see Page.php for `services` implementation
     * @type {Promise<{ id: number, title: string, description: string, lowPrice: number, highPrice: number, iconURL?: string, imageURL?: string }[]>}
     */
    static #servicePromise = null;

    /** @type {HTMLUListElement} */
    #resultsWrapper;

    /** @type {HTMLLIElement[]} */
    #results = [];

    /** @type {HTMLAnchorElement} */
    #dropdown;

    /** @type {HTMLButtonElement} */
    #submitButton

    /** @type {number} */
    #dropdownInterval = null;

    /** @type {number} */
    #currentServiceId = 0;

    static get services() {
        if (!this.#servicePromise) {
            this.#servicePromise = new Promise(resolve => {
                fetch(`/home/services`)
                .then(response => response.json())
                .then(resolve);
            });
        }

        return this.#servicePromise;
    }

    get value() {
        return this.#currentServiceId;
    }

    constructor() {
        super();

        this.#dropdown = document.createElement("a");
        this.#dropdown.href = "javascript:;";
        this.#dropdown.className = "hi-service-form--dropdown";
        this.#dropdown.textContent = "What do you need help with?";

        this.#dropdown.addEventListener("click", () => this.#results[0].focus());

        this.#resultsWrapper = document.createElement("ul");
        this.#resultsWrapper.tabIndex = -1;
        this.#resultsWrapper.className = "hi-service-form--results";

        this.#submitButton = document.createElement("button");
        this.#submitButton.className = "button medium hi-service-form--button";
        this.#submitButton.textContent = "Start Your Project";

        this.#submitButton.addEventListener("click", () => {
            if (this.value) {
                ServiceFormElement.services.then(services => {
                    // alert("[TEMP] " + services.find(service => service.id === this.value).title + " selected");
                    window.location.replace(`https://app.helpfulheroes.com/new-project?service=${services.find(service => service.id === this.value).title}`);
                });
            }
            else {
                // alert("[TEMP] No service selected");
                window.location.replace("https://app.helpfulheroes.com/");
            }
        });

        this.append(this.#dropdown, this.#resultsWrapper, this.#submitButton);

        ServiceFormElement.services.then(services => {
            for (let service of services) {
                let result = document.createElement("a");
                result.href = "javascript:;";
                result.className = "hi-service-form--result";
                result.dataset.id = service.id;

                let title = document.createElement("span");
                title.className = "hi-service-form--title";
                title.textContent = service.title;

                let price = document.createElement("span");
                price.className = "hi-service-form--price";
                price.textContent = `$${service.lowPrice} - $${service.highPrice}`;

                result.append(title, price);

                result.addEventListener("click", () => {
                    this.#dropdown.textContent = title.textContent;
                    this.#dropdown.classList.add("populated");

                    this.#currentServiceId = service.id;

                    this.#submitButton.focus();
                });

                this.#results.push(result);
                this.#resultsWrapper.append(result);
            }
        });
    }
}

class TestimonialsElement extends HTMLElement {
    #delay = 0;
    index = 0;

    /**
     * Shift the carousel by the given direction
     * @param {"left" | "right"} direction
     */
    shift(direction) {
        if (performance.now() < this.#delay) return;

        let wrapper = this.querySelector(".testimonial-wrapper");

        wrapper.classList.toggle("left", direction === "left");
        wrapper.classList.toggle("right", direction === "right");

        let current = this.querySelector(".testimonial.current") ?? this.testimonials[0];
        let next;

        if (direction === "left") {
            next = current.previousElementSibling ?? this.testimonials.slice(-1)[0];
        }

        if (direction === "right") {
            next = current.nextElementSibling ?? this.testimonials[0];
        }

        current.classList.remove("current");
        next.classList.add("current");

        this.#delay = performance.now() + 450;
    }

    constructor() {
        super();

        this.testimonials = Array.from(this.querySelectorAll(".testimonial"));
        this.testimonials[0].classList.add("current");

        this.querySelector(".arrows .prev").addEventListener("click", () => this.shift("left"));

        this.querySelector(".arrows .next").addEventListener("click", () => this.shift("right"));
    }
}

class ResourcesElement extends HTMLElement {
    #list;
    #showMore;

    /**
     * Filter posts based on the given `ResourceCategory` slug
     * @param {string} [slug]
     */
    filter(slug) {
        if (!this.loading) {
            this.#showMore.classList.add("loading");

            return fetch(`${this.base}json?limit=${this.paginate}&offset=${this.#list.children.length}&category=${this.category}`)
            .then(res => res.json())
            .then(payload => {
                /** @type {HTMLTemplateElement} */
                let template = this.querySelector("template[name=resource]");

                for (let resource of payload.resources) {
                    let element = template.content.cloneNode(true);

                    element.querySelector("[for=link]").setAttribute("href", resource.link);
                    element.querySelector("[for=title]").textContent = resource.title;
                    element.querySelector("[for=summary]").textContent = resource.summary;
                    // element.querySelector("[for=publishedDate]").textContent = resource.publishedDate;

                    this.#list.append(element);
                }

                this.#showMore.classList.remove("loading");
                this.#showMore.classList.toggle("disabled", payload.total <= this.#list.children.length);
            });
        }
    }

    get loading() {
        return this.#showMore.classList.contains("loading");
    }

    set category(slug) {
        this.setAttribute("category", slug ?? "");

        this.querySelectorAll(`.hi-resources--categories a`).forEach(link => {
            link.classList.toggle("current", link.dataset.category === slug);
        });

        if (slug) {
            history.pushState(null, document.title, `${this.base}filter/${slug}`);
        }
        else {
            history.pushState(null, document.title, this.base);
        }

        this.#list.classList.add("dismiss");
        this.#list.style.height = this.#list.clientHeight + "px";

        setTimeout(() => {
            this.#list.replaceChildren("");
            this.filter(slug)
            .then(() => requestAnimationFrame(() => {
                this.#list.classList.remove("dismiss");
                this.#list.style.height = "unset";
            }));
        }, 500);
    }

    get category() {
        return this.getAttribute("category") ?? "";
    }

    get paginate() {
        return parseInt(this.getAttribute("paginate")) || 10;
    }

    get base() {
        return this.getAttribute("base") || "/resources/";
    }

    constructor() {
        super();

        this.#list = document.createElement("div");
        this.#list.className = "hi-resources--list dismiss";

        this.#showMore = document.createElement("button");
        this.#showMore.className = "hi-resources--show-more";
        this.#showMore.textContent = "Show More";

        this.#showMore.addEventListener("click", () => {
            this.filter(this.category);
        });

        this.append(this.#list, this.#showMore);

        fetch(`${this.base}categories`)
        .then(res => res.json())
        .then(categories => {
            let categoryWrapper = document.createElement("div");
            categoryWrapper.className = "hi-resources--categories";

            for (let category of [ { "Title": "All Posts", "Slug": "" }, ...categories ]) {
                let link = document.createElement("a");
                link.dataset.category = category.Slug;
                link.href = "javascript:;";
                link.textContent = category.Title;

                link.className = this.category === category.Slug ? "current" : "";

                link.addEventListener("click", () => this.category = link.dataset.category);

                categoryWrapper.append(link);
            }

            this.prepend(categoryWrapper);
        });

        this.filter(this.category)
        .then(() => requestAnimationFrame(() => this.#list.classList.remove("dismiss")));
    }
}

customElements.define("hi-service-form", ServiceFormElement);
customElements.define("hi-testimonials", TestimonialsElement);
customElements.define("hi-resources", ResourcesElement);

function initMobileMenuControls() {
	// var items = $("#menu .l1-item").length;
    //
	// $("#menu .l1-item").on("click", function() {
	// 	var itemCount = $(this).find(".sub-menu li").length;
    //
	// 	if (itemCount >= items) {
	// 		$("#menu-wrap .menu-btm").css("transform", "translateY(" + (((itemCount + 1) - items) * 52).toString() + "px)");
	// 	}
	// });
    //
	// $(document).on("click", "#menu .l1-item .btn-close-sub", function() {
	// 	$("#menu-wrap .menu-btm").css("transform", "none");
	// });
}


$(function() {

    //---------------------------------------------------------
    //  Header functionality
    //---------------------------------------------------------
    if (Foundation.MediaQuery.current == 'small') {
            var threshold = 100;
    } else {
        var threshold = $('#page-heading').hasClass('height-standard') ? 100 : 200;
    }
    if ($(document).scrollTop() > threshold) {
        $('#header').addClass('solid');
    }

    $(window).scroll(function() {
        $( '.fade-content:in-viewport( 900 )' ).addClass('show-content');

        if (Foundation.MediaQuery.current == 'small') {
            var scrollThreshold = 100;
        } else {
            var scrollThreshold = $('#page-heading').hasClass('height-Standard') ? 100 : 200;
        }

        if ($(document).scrollTop() > scrollThreshold) {
            $('#header').addClass('solid');
        }
        else {
            $('#header').removeClass('solid');
        }
    });
    //---------------------------------------------------------


    //---------------------------------------------------------
    //  Dropdown Navigation Menu
    //---------------------------------------------------------
    $(document).on('click', '.icon-menu', function() {
        $(this).toggleClass('open');
        $('#dropdown-menu').toggleClass('open');
        $('#header').toggleClass('menu-open');
        $('body').toggleClass('menu-open');
        if ($(this).hasClass('open')) {
            $('#nav-shade').fadeIn(300);
        } else {
            $('#nav-shade').fadeOut(300);
        }
    });

    $(document).on('click', '#nav-shade', function() {
        $('.icon-menu').removeClass('open');
        $('#dropdown-menu').removeClass('open');
        $('#header').removeClass('menu-open');
        $('body').removeClass('menu-open');
        $('#nav-shade').fadeOut(300);
    });

    //---------------------------------------------------------


    if ($('.image-slider').length > 0) {
        $('.image-slider').slick({
            infinite: true,
            slidesToShow: 6,
            slidesToScroll: 1,
            // variableWidth: true,
            // autoplay:true,
            speed: 600,
            dots: false,
            // appendDots: $('.image-slider-wrap'),
            // appendArrows: $('.image-slider-wrap'),
            // prevArrow:'<a class="slider-prev"><i class="fal fa-chevron-left"></i></a>',
            // nextArrow:'<a class="slider-next"><i class="fal fa-chevron-right"></i></a>',
            responsive: [
              {
                breakpoint: 500,
                settings: {
                    arrows: false,
                    slidesToShow: 2,
                }
              },
            ]
        });
    }


    if ($('.featured-service-slider').length > 0) {
        $('.featured-service-slider').slick({
            infinite: true,
            slidesToShow: 4,
            slidesToScroll: 1,
            // autoplay:true,
            speed: 600,
            dots: false,
            // appendDots: $('.image-slider-wrap'),
            // appendArrows: $('.image-slider-wrap'),
            // prevArrow:'<a class="slider-prev"><i class="fal fa-chevron-left"></i></a>',
            // nextArrow:'<a class="slider-next"><i class="fal fa-chevron-right"></i></a>',
            responsive: [
              {
                breakpoint: 500,
                settings: {
                    arrows: false,
                    slidesToShow: 2,
                }
              },
            ]
        });
    }




    //---------------------------------------------------------
    // Page Specific JS
    //---------------------------------------------------------

    if ($('.HomePage').length > 0) {
        // gsap.from(".top-image", { duration: 1, y:50, x: 50, opacity:0 });

        // gsap.from(".bottom-image", { duration: 1.5, x: 75, opacity:0 });
    }

    if ($('.FAQPage').length > 0) {

        $( document ).ready(function() {
            var filterID = 0;
            $(".category-button").each(function(index, element) {
                if($(this).hasClass('medium')){
                    filterID =$(this).data('id');
                    $('#category-title').html($(this).html());
                }
            });

            $(".accordion-item").each(function(index, element) {
                if($(this).data('category')==filterID){
                    $(this).show();
                }
                else{
                    $(this).hide();
                }
            });

        });

        $(document).on('click', '.category-button', function() {
            var filterID = $(this).data('id');
            $('#category-title').html($(this).html());

            $('.category-button').removeClass('medium');
            $('.category-button').removeClass('outline');
            $('.category-button').addClass('outline');
            $(this).removeClass('outline');
            $(this).addClass('medium');

            $(".accordion-item").each(function(index, element) {
                if($(this).data('category')==filterID){
                    $(this).show();
                }
                else{
                    $(this).hide();
                }
            });
        });

    }

    //---------------------------------------------------------

    if ($('.elements__hidrentisnowelement').length > 0) {

        let currentImageIndex = 1;

        function changeBackgroundImage() {
           
            if(currentImageIndex == 1){
                $('.svg1').removeClass('active');
                $('.bg1').removeClass('active');
                $('.svg2').addClass('active');
                $('.bg2').addClass('active');
                currentImageIndex = 2;
            }
            else{
                $('.svg1').addClass('active');
                $('.bg1').addClass('active');
                $('.svg2').removeClass('active');
                $('.bg2').removeClass('active');
                currentImageIndex = 1;
            }
        }
        
        setInterval(changeBackgroundImage, 7000);
    }


    if (document.body.classList.contains("ResourcePage")) {
        document.querySelector(".share-copy").addEventListener("click", event => {
            if (location.protocol === "https:") {
                if ("share" in navigator) {
                    navigator.share({
                        url: location.href,
                        title: document.title
                    })
                }
                else {
                    let parent = event.currentTarget.parentElement;

                    navigator.clipboard.writeText(location.href).then(() => parent.append("Link Copied to Clipboard"));
                }
            }
        });
    }

    $(document).on('submit', '#ffsignup-form', function(e) {
        e.preventDefault();
        var self = $(this);
        $.ajax({type: "GET", url: "api/v1/ffsignup", dataType: 'json', data: self.serialize() })
        .done(function(data) {

        })
        .fail (function (xhr) {
            console.log('Error: ' + xhr.responseText);
        });
        $('.form').hide();
        $('.form-submit').show();
    });


    $(document).on('submit', '#rms-form', function(e) {
        e.preventDefault();

        $("#rms-form").hide();
        $("#rms-pending").show();

        var self = $(this);
        $.ajax({type: "GET", url: "api/v1/rmsrequest", dataType: 'json', data: self.serialize() })
        .done(function(data) {
            $("#rms-confirm").show();
            $("#rms-pending").hide();
            $("#rms-container").hide();

            $("#rms-pin").html(data.result);
        })
        .fail (function (xhr) {
            console.log('Error: ' + xhr.responseText);
        });
    });


        $(document).on('click', '#close-alert', function() {
            $('.alert-bar').addClass('hide');
            $('body').removeClass('has-alert');
            $.ajax({type: "GET", url: "api/v1/clearalert", dataType: 'json' })
            .done(function(data) {
            })
            .fail (function (xhr) {
                console.log('Error: ' + xhr.responseText);
            });
        });

});
